import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { track, getVisitorId, getSessionId } from '../lib/tracker';

const CartContext = createContext(null);
const STORAGE_KEY = 'byanfal_cart_v1';

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [hydrated, setHydrated] = useState(false);
    const syncTimer = useRef(null);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) setItems(JSON.parse(stored));
        } catch (error) {
            console.error('Failed to load cart from storage:', error);
        }
        setHydrated(true);
    }, []);

    useEffect(() => {
        // Never persist/sync until we've hydrated from localStorage,
        // otherwise the initial empty state wipes the saved cart.
        if (!hydrated) return;
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (error) {
            console.error('Failed to persist cart to storage:', error);
        }
        if (syncTimer.current) clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
            const sub = items.reduce((s, i) => s + i.price * i.quantity, 0);
            api.put('/cart', {
                visitor_id: getVisitorId(),
                session_id: getSessionId(),
                items,
                subtotal: sub,
            }).catch(() => {});
        }, 700);
    }, [items, hydrated]);

    const lineKey = (it) => `${it.product_id}|${it.size || ''}|${it.color || ''}|${it.special_request || ''}`;

    const addItem = (item) => {
        setItems((prev) => {
            const key = lineKey(item);
            const idx = prev.findIndex((i) => lineKey(i) === key);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
                return next;
            }
            return [...prev, { ...item, quantity: item.quantity || 1 }];
        });
        track('add_to_cart', { product_id: item.product_id, product_name: item.name, path: window.location.pathname });
    };

    const updateQty = (key, qty) => {
        setItems((prev) => prev.map((i) => (lineKey(i) === key ? { ...i, quantity: Math.max(1, qty) } : i)));
    };

    const removeItem = (key) => {
        setItems((prev) => prev.filter((i) => lineKey(i) !== key));
    };

    const clear = () => setItems([]);

    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    const count = items.reduce((sum, i) => sum + i.quantity, 0);

    return (
        <CartContext.Provider
            value={{ items, addItem, updateQty, removeItem, clear, subtotal, count, drawerOpen, setDrawerOpen, lineKey }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error('useCart must be used within CartProvider');
    return ctx;
};
